import { Shield, Compass, Zap, Scale, FileSignature } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { useLang } from "@/i18n";

const GALLERIA_IMG =
  "https://images.unsplash.com/photo-1633355900844-d9e0c8be9659?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxtaWxhbiUyMGFyY2hpdGVjdHVyZSUyMG1vZGVybnxlbnwwfHx8fDE3OTE3OTMwNTh8MA&ixlib=rb-4.1.0&q=85";

const CONTENT = {
  it: {
    seoTitle: "Chi Siamo — Lo Studio e l'Assetto Societario | Mediolanum Consulting",
    seoDesc: "Mediolanum Consulting Srl a Milano: una boutique di consulenza a presidio diretto dei soci. Manifesto, assetto societario e pilastri etici.",
    eyebrow: "Chi Siamo",
    manifestoEyebrow: "Il Manifesto",
    manifestoTitle: "Una boutique di consulenza, non una fabbrica di pareri.",
    manifesto: [
      "Mediolanum Consulting Srl nasce a Milano nel novembre 2025 con una convinzione precisa: le decisioni societarie, fiscali e straordinarie che determinano il destino di un'impresa richiedono attenzione sartoriale, non processi industriali.",
      "Per questo ogni mandato è seguito direttamente dal team della società. Analisi approfondita ed un unico interlocutore responsabile dall'inizio alla chiusura dell'operazione.",
      "Dalla consulenza fiscale e tributaria alla redazione di business plan, dalla pianificazione strategica all'assistenza nelle operazioni straordinarie e nelle ristrutturazioni: il perimetro dei servizi è quello iscritto nel nostro oggetto sociale, la qualità è quella che ci poniamo come condizione di esistenza.",
    ],
    assettoEyebrow: "L'Assetto Societario",
    assettoCopy: "Dati da visura camerale — Camera di Commercio di Milano, Monza Brianza e Lodi.",
    assetto: [
      {
        title: "Amministratore Unico",
        copy: "Dott. Riccardo Carugati, con tutti i poteri per l'amministrazione ordinaria e straordinaria della società. Ogni mandato ha un unico, chiaro interlocutore.",
      },
      {
        title: "Attività Iscritta",
        copy: "Consulenza e organizzazione aziendale (ATECO 70.20.09): consulenza fiscale, tributaria e societaria, business plan, pianificazione strategica e ristrutturazioni.",
      },
    ],
    pilastriEyebrow: "I Pilastri Etici",
    pilastriTitle: "Tre impegni che non negoziamo mai.",
    pilastri: [
      { title: "Riservatezza Assoluta", copy: "Ogni mandato è coperto da protocolli di confidenzialità stringenti. Le informazioni dei clienti non lasciano mai il perimetro dello studio." },
      { title: "Indipendenza d'Analisi", copy: "Nessun conflitto di interesse: non vendiamo prodotti finanziari, non riceviamo provvigioni. Il nostro unico mandato è l'interesse del cliente." },
      { title: "Flessibilità Esecutiva", copy: "Struttura snella e soci direttamente operativi su ogni dossier. Decidiamo e agiamo con la velocità che le operazioni straordinarie richiedono." },
    ],
    direzioneEyebrow: "Direzione Professionale",
    direzioneText: "La direzione tecnica dello studio è affidata al Dott. Riccardo Carugati, Dottore Commercialista iscritto all'Ordine di Milano.",
  },
  en: {
    seoTitle: "About Us — The Firm and its Structure | Mediolanum Consulting",
    seoDesc: "Mediolanum Consulting Srl in Milan: a boutique advisory firm with direct partner involvement. Manifesto, corporate structure and ethical pillars.",
    eyebrow: "About Us",
    manifestoEyebrow: "Our Manifesto",
    manifestoTitle: "A boutique advisory firm, not an opinion factory.",
    manifesto: [
      "Mediolanum Consulting Srl was founded in Milan in November 2025 with a precise conviction: the corporate, tax and extraordinary decisions that determine a company's destiny require tailor-made attention, not industrial processes.",
      "This is why every engagement is handled directly by the firm's team. In-depth analysis and a single accountable point of contact, from the beginning to the closing of the transaction.",
      "From tax and fiscal advisory to business plan drafting, from strategic planning to assistance in extraordinary transactions and restructurings: our service perimeter is the one registered in our corporate purpose, and the quality is the one we set as a condition of our existence.",
    ],
    assettoEyebrow: "Corporate Structure",
    assettoCopy: "Data from the Chamber of Commerce registry — Milan, Monza Brianza and Lodi.",
    assetto: [
      {
        title: "Sole Director",
        copy: "Dr. Riccardo Carugati, holding all powers for the ordinary and extraordinary management of the company. Every engagement has a single, clear point of contact.",
      },
      {
        title: "Registered Activity",
        copy: "Business consulting and organisation (ATECO 70.20.09): tax, fiscal and corporate advisory, business plans, strategic planning and restructurings.",
      },
    ],
    pilastriEyebrow: "Our Ethical Pillars",
    pilastriTitle: "Three commitments we never negotiate.",
    pilastri: [
      { title: "Absolute Confidentiality", copy: "Every engagement is covered by strict confidentiality protocols. Client information never leaves the firm's perimeter." },
      { title: "Independence of Analysis", copy: "No conflicts of interest: we do not sell financial products and we do not receive commissions. Our only mandate is the client's interest." },
      { title: "Executive Agility", copy: "A lean structure with partners directly operational on every file. We decide and act with the speed that extraordinary transactions require." },
    ],
    direzioneEyebrow: "Professional Leadership",
    direzioneText: "The firm's technical leadership is entrusted to Dr. Riccardo Carugati, Chartered Accountant registered with the Milan Order.",
  },
};

const ASSETTO_ICONS = [FileSignature, Scale];
const PILASTRI_ICONS = [Shield, Compass, Zap];

export default function ChiSiamo() {
  const { lang } = useLang();
  const c = CONTENT[lang];

  return (
    <main data-testid="chi-siamo-page">
      <Seo title={c.seoTitle} description={c.seoDesc} />
      <section className="relative flex min-h-[62vh] items-end overflow-hidden" data-testid="chi-siamo-hero">
        <img src={GALLERIA_IMG} alt="Galleria Vittorio Emanuele II, Milano" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/70 via-navy-950/60 to-navy-950" />
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-20 pt-44 sm:px-8">
          <Reveal>
            <p className="eyebrow">{c.eyebrow}</p>
            <h1 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-tight text-slate-100 sm:text-5xl lg:text-6xl">
              {lang === "en"
                ? <>The rigour of the Milanese method, <span className="text-gold-gradient italic">at the service of businesses.</span></>
                : <>Il rigore del metodo milanese, <span className="text-gold-gradient italic">al servizio delle imprese.</span></>}
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="manifesto-section">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-2">
          <SectionHeading eyebrow={c.manifestoEyebrow} title={c.manifestoTitle} />
          <Reveal delay={0.15}>
            <div className="space-y-5 text-base font-light leading-relaxed text-slate-300">
              {c.manifesto.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-white/10 bg-navy-900/40 py-24" data-testid="assetto-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow={c.assettoEyebrow}
            title={lang === "en"
              ? <>Transparency first: <span className="text-gold-gradient italic">this is who we are.</span></>
              : <>Trasparenza prima di tutto: <span className="text-gold-gradient italic">ecco chi siamo.</span></>}
            copy={c.assettoCopy}
          />
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
            {c.assetto.map((a, i) => {
              const Icon = ASSETTO_ICONS[i];
              return (
                <Reveal key={a.title} delay={i * 0.1}>
                  <div className="card-surface card-surface-hover h-full rounded-2xl p-8" data-testid={`assetto-card-${i}`}>
                    <Icon className="text-gold" size={28} />
                    <h3 className="mt-6 font-serif text-xl text-slate-100 sm:text-2xl">{a.title}</h3>
                    <p className="mt-4 text-sm font-light leading-relaxed text-slate-400">{a.copy}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="pilastri-section">
        <SectionHeading eyebrow={c.pilastriEyebrow} title={c.pilastriTitle} />
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {c.pilastri.map((p, i) => {
            const Icon = PILASTRI_ICONS[i];
            return (
              <Reveal key={p.title} delay={i * 0.1}>
                <div className="card-surface card-surface-hover h-full rounded-2xl p-8" data-testid={`pilastro-${i}`}>
                  <Icon className="text-gold" size={28} />
                  <h3 className="mt-6 font-serif text-xl text-slate-100 sm:text-2xl">{p.title}</h3>
                  <p className="mt-4 text-sm font-light leading-relaxed text-slate-400">{p.copy}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-28 sm:px-8" data-testid="direzione-section">
        <Reveal>
          <div className="card-surface rounded-2xl p-8 sm:p-12">
            <p className="eyebrow">{c.direzioneEyebrow}</p>
            <p className="mt-6 max-w-3xl font-serif text-xl font-light leading-relaxed text-slate-200 sm:text-2xl">
              {c.direzioneText}
            </p>
            <p className="mt-5 max-w-2xl text-sm font-light leading-relaxed text-slate-400">
              PEC: <a href="mailto:riccardo.carugati@odcecmilano.it" className="text-gold-light transition-colors hover:text-gold" data-testid="direzione-pec">riccardo.carugati@odcecmilano.it</a>
            </p>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
